import { useState, useCallback, useEffect } from 'react';
import { compareMonths } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingUp, TrendingDown, Minus, GitCompare } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => n?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) ?? '0';

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(+y, +mo - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export function CompareMonthsPanel({ monthOptions, state, district, onClose }) {
  const [monthA, setMonthA] = useState(monthOptions[1] || '');
  const [monthB, setMonthB] = useState(monthOptions[0] || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!monthA || !monthB || monthA === monthB) return;
    setLoading(true);
    try {
      const st = state && state !== 'all' ? state : undefined;
      const dist = district && district !== 'all' ? district : undefined;
      const res = await compareMonths(monthA, monthB, st, dist);
      setData(res.data);
    } catch { toast.error('Failed to load comparison'); }
    finally { setLoading(false); }
  }, [monthA, monthB, state, district]);

  useEffect(() => { load(); }, [load]);

  const metrics = [
    { key: 'sold_value', label: 'Sold Value', prefix: 'TJS ', color: 'blue' },
    { key: 'collected', label: 'Collected', prefix: 'TJS ', color: 'emerald' },
    { key: 'pending', label: 'Pending', prefix: 'TJS ', color: 'rose' },
    { key: 'active_partners', label: 'Active Partners', prefix: '', color: 'purple' },
    { key: 'deliveries', label: 'Deliveries', prefix: '', color: 'amber' },
    { key: 'avg_collection_pct', label: 'Avg Collection %', prefix: '', suffix: '%', color: 'teal' },
  ];

  return (
    <div className="rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-card overflow-hidden" data-testid="compare-months-panel">
      <div className="bg-indigo-50 dark:bg-indigo-950/30 px-4 py-3 border-b border-indigo-200 dark:border-indigo-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitCompare className="w-4 h-4 text-indigo-600" />
          <span className="text-sm font-bold text-indigo-800 dark:text-indigo-300">Compare Months</span>
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClose} data-testid="close-compare">Close</Button>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={monthA} onValueChange={setMonthA}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="compare-month-a"><SelectValue placeholder="Month A" /></SelectTrigger>
            <SelectContent>{monthOptions.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          <Select value={monthB} onValueChange={setMonthB}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="compare-month-b"><SelectValue placeholder="Month B" /></SelectTrigger>
            <SelectContent>{monthOptions.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          {monthA === monthB && <span className="text-[10px] text-amber-600">Select different months</span>}
        </div>

        {loading && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {data && !loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="compare-metrics">
            {metrics.map(({ key, label, prefix, suffix, color }) => {
              const a = data.month_a[key] ?? 0;
              const b = data.month_b[key] ?? 0;
              const change = data.changes[key] || {};
              const improved = change.improved;
              const ChangeIcon = change.diff > 0 ? TrendingUp : change.diff < 0 ? TrendingDown : Minus;
              const changeColor = improved ? 'text-emerald-600' : change.diff === 0 ? 'text-muted-foreground' : 'text-rose-600';

              return (
                <div key={key} className="rounded-lg border p-3 bg-muted/20 hover:bg-muted/40 transition-colors" data-testid={`compare-metric-${key}`}>
                  <p className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-2">{label}</p>
                  <div className="flex items-end justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-14">{monthLabel(data.month_a.month)}</span>
                        <span className="text-sm font-bold tabular-nums">{prefix}{fmt(a)}{suffix || ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-14">{monthLabel(data.month_b.month)}</span>
                        <span className="text-sm font-bold tabular-nums">{prefix}{fmt(b)}{suffix || ''}</span>
                      </div>
                    </div>
                    <div className={`flex flex-col items-end ${changeColor}`}>
                      <ChangeIcon className="w-4 h-4" />
                      <span className="text-xs font-bold tabular-nums">{change.pct > 0 ? '+' : ''}{change.pct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
